import { prepareRichInline, walkRichInlineLineRanges, materializeRichInlineLineRange, measureRichInlineStats } from '@chenglou/pretext/rich-inline';

export const PORTFOLIO_EVENT_TYPES = [
  "FiveWordStory",
  "Story",
  "Poem",
  "Quote",
  "Achievement",
];

export function getMaxHeightForScreen(language = "") {
  const isIndic = ["sindhi", "hindi", "marathi"].includes(language.toLowerCase());
  const screenWidth = typeof window !== "undefined" ? window.innerWidth : 1024;

  const bookWidth = 300;

  let padding;
  let normalFontSize;
  let baseFirstPageMaxHeight;
  let baseSubsequentPageMaxHeight;

  if (screenWidth <= 480) {
    padding = 10;
    normalFontSize = 17;
  } else if (screenWidth <= 768) {
    padding = 15;
    normalFontSize = 14;
  } else {
    padding = 20;
    normalFontSize = 16;
  }

  // Adjusting max heights to be closer to physical bounds (500 - 40 padding - 2 border = 458 max height)
  // This prevents 'premature cuts' where the last line spills over to a mostly empty page
  if (screenWidth > 1024) { // Large screens
    baseSubsequentPageMaxHeight = 455;
    baseFirstPageMaxHeight = 405;
  } else if (screenWidth > 768) { // Medium screens
    baseSubsequentPageMaxHeight = 455;
    baseFirstPageMaxHeight = 405;
  } else { // Small screens (mobile)
    baseSubsequentPageMaxHeight = 455;
    baseFirstPageMaxHeight = 405;
  }

  const contentWidth = bookWidth - (padding * 2) - 2; // -2px for .page borders
  const sindhiFontSize = 18;

  // Derive pixel heights from CSS (line-height: 1.4, margin bottom: 16px)
  const normalLineHeightPx = normalFontSize * 1.4;
  const sindhiLineHeightPx = sindhiFontSize * 1.4;
  const lineHeightPx = isIndic ? sindhiLineHeightPx : normalLineHeightPx;
  const paragraphMarginPx = 16;

  return {
    widthInt: contentWidth,
    subsequentPageMaxHeight: baseSubsequentPageMaxHeight,
    firstPageMaxHeight: baseFirstPageMaxHeight,
    normalFont: `${normalFontSize}px "Itim", sans-serif`,
    normalFontSize: normalFontSize,
    sindhiFont: `${sindhiFontSize}px "SHREE-DEV7-0714", Arial, sans-serif`,
    sindhiFontSize: sindhiFontSize,
    lineHeightPx,
    paragraphMarginPx
  };
}

function parseHtmlToRichInline(htmlString, baseFont) {
  const items = [];
  const itemTags = [];

  const regex = /(<[^>]+>)|([^<]+)/g;
  let match;
  let openTags = [];

  while ((match = regex.exec(htmlString)) !== null) {
    if (match[1]) {
      const tag = match[1];
      if (tag.startsWith('</')) {
        openTags.pop();
      } else if (!tag.endsWith('/>') && tag !== '<br>' && tag !== '<br/>') {
        openTags.push(tag);
      } else if (tag === '<br>' || tag === '<br/>') {
        items.push({ text: '\n', font: baseFont });
        itemTags.push([...openTags]);
      }
    } else if (match[2]) {
      const text = match[2];
      const decodedText = text.replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>');

      let font = baseFont;
      if (openTags.some(t => t.startsWith('<b') || t.startsWith('<strong'))) {
        font = 'bold ' + font;
      }
      if (openTags.some(t => t.startsWith('<i') || t.startsWith('<em'))) {
        font = 'italic ' + font;
      }

      items.push({ text: decodedText, font });
      itemTags.push([...openTags]);
    }
  }

  return { items, itemTags };
}

function escapeHtml(text) {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function measureEntryHeightPretext(entry, eventType, config) {
  let html = "";
  const hasTitle = entry.title && String(entry.title).trim().length > 0;
  const title = hasTitle ? escapeHtml(String(entry.title)) : "";
  const bodyHtml = entry.body ? String(entry.body) : "";

  if (hasTitle) {
    const bullet = eventType === "Quote" || eventType === "Achievement" ? "• " : "";
    html += `<div>${bullet}${title}</div>`; // Simplify for measurement
  }
  if (bodyHtml) {
    html += `<div>${bodyHtml}</div>`;
  }
  if (!hasTitle && !bodyHtml) {
    html = title;
  }

  // Strip block tags for measurement
  const innerHtml = html.replace(/<[^>]+>/g, ' ');
  const { items } = parseHtmlToRichInline(innerHtml, config.normalFont);
  
  if (items.length === 0) return 0;
  try {
    const prepared = prepareRichInline(items);
    const stats = measureRichInlineStats(prepared, config.widthInt);
    
    // Base height + margins for title/body
    let entryHeight = (stats.lineCount || 1) * config.lineHeightPx;
    if (hasTitle) entryHeight += 14; // margin-bottom: 10px + 4px
    if (!hasTitle) entryHeight += 12; // margin: 6px 0
    return entryHeight;
  } catch(e) {
    console.error(e);
    return config.lineHeightPx * 2; // Safe fallback
  }
}

function splitHtmlByHeightPretext(htmlContent, config, isSindhi) {
  if (!htmlContent) return [htmlContent];
  if (typeof document === 'undefined') return [htmlContent]; 

  const pages = [];
  let currentPageContent = "";
  
  let currentMaxHeight = config.firstPageMaxHeight;
  let currentHeight = 0;

  const paragraphs = htmlContent.split(/(<p[^>]*>[\s\S]*?<\/p>)/gi).filter(p => p.trim());
  const baseFont = isSindhi ? config.sindhiFont : config.normalFont;

  const pushPage = () => {
    if (currentPageContent.trim()) {
      pages.push(currentPageContent.trim());
    }
    currentPageContent = "";
    currentMaxHeight = config.subsequentPageMaxHeight;
    currentHeight = 0;
  };

  for (let pIndex = 0; pIndex < paragraphs.length; pIndex++) {
    const paragraph = paragraphs[pIndex];
    const isP = paragraph.toLowerCase().startsWith('<p');
    const innerHtml = isP ? paragraph.replace(/^<p[^>]*>/i, '').replace(/<\/p>$/i, '') : paragraph;

    const { items, itemTags } = parseHtmlToRichInline(innerHtml, baseFont);
    
    if (items.length === 0) continue;

    let prepared;
    try {
      prepared = prepareRichInline(items);
    } catch (e) {
      console.error("pretext prepare error", e);
      currentPageContent += paragraph;
      continue;
    }

    let paragraphHtml = "";
    
    if (currentPageContent !== "") {
      currentHeight += config.paragraphMarginPx;
      if (currentHeight > currentMaxHeight) {
        pushPage();
      }
    }

    walkRichInlineLineRanges(prepared, config.widthInt, (lineRange) => {
      if (currentHeight + config.lineHeightPx > currentMaxHeight) {
        if (paragraphHtml) {
          currentPageContent += isP ? `<p>${paragraphHtml}</p>` : paragraphHtml;
          paragraphHtml = "";
        }
        pushPage();
      }
      
      currentHeight += config.lineHeightPx;

      const line = materializeRichInlineLineRange(prepared, lineRange);
      
      for (const fragment of line.fragments) {
        const text = fragment.text;
        const tags = itemTags[fragment.itemIndex] || [];
        
        let fragHtml = "";
        for (const tag of tags) fragHtml += tag;
        
        if (text === '\n') {
          fragHtml += '<br>';
        } else {
          fragHtml += escapeHtml(text);
        }
        
        for (let i = tags.length - 1; i >= 0; i--) {
           const match = tags[i].match(/<([a-zA-Z0-9]+)[^>]*>/);
           if (match) fragHtml += `</${match[1]}>`;
        }
        
        paragraphHtml += fragHtml;
      }
    });

    if (paragraphHtml) {
      currentPageContent += isP ? `<p>${paragraphHtml}</p>` : paragraphHtml;
    }
  }

  if (currentPageContent.trim()) {
    pages.push(currentPageContent.trim());
  }

  return pages.length > 0 ? pages : [htmlContent];
}

export async function processPortfolioNewToPages(portfolioData) {
  if (typeof document !== 'undefined' && document.fonts) {
    try {
      await document.fonts.load('16px "Itim"');
      await document.fonts.load('18px "SHREE-DEV7-0714"');
    } catch (e) {
      console.warn("Font loading error:", e);
    }
    await document.fonts.ready;
  }

  const processed = [];

  const fiveWordStories = Array.isArray(portfolioData?.FiveWordStories)
    ? portfolioData.FiveWordStories
    : [];
  processed.push(
    ...buildGroupedPagesForEntries(
      fiveWordStories.map((s) => ({
        title: typeof s === "string" ? s : s?.name || "",
        body: typeof s === "string" ? "" : s?.description || "",
      })),
      "FiveWordStory",
      "Five Word Stories"
    )
  );

  const stories = Array.isArray(portfolioData?.stories)
    ? portfolioData.stories.map((story) => ({
      eventType: "Story",
      eventId: story,
    }))
    : [];
  for (const item of stories) {
    const htmlDescription = item.eventId?.description || "";
    const lang = (item.eventId?.language || "").toLowerCase();
    let config = getMaxHeightForScreen(lang);
    
    const titleText = item.eventId?.name || "";
    const extraTitleLines = Math.max(0, Math.ceil(titleText.length / 22) - 1);
    config.firstPageMaxHeight -= (extraTitleLines * 30);

    if (htmlDescription) {
      const isIndic = ["sindhi", "hindi", "marathi"].includes(lang);
      const pages = splitHtmlByHeightPretext(
        htmlDescription,
        config,
        isIndic
      );
      if (pages.length > 1) {
        pages.forEach((pageContent, index) => {
          processed.push({
            ...item,
            eventId: {
              ...item.eventId,
              name: index === 0 ? item.eventId.name : `${item.eventId.name} (cont.)`,
              description: pageContent,
            },
            pageNumber: index + 1,
            isMultiPage: true,
            totalPages: pages.length,
          });
        });
      } else {
        processed.push({
          ...item,
          eventId: { ...item.eventId, description: htmlDescription },
          isMultiPage: false,
        });
      }
    } else {
      processed.push({ ...item, isMultiPage: false });
    }
  }

  const poems = Array.isArray(portfolioData?.poems)
    ? portfolioData.poems.map((poem) => ({ eventType: "Poem", eventId: poem }))
    : [];

  for (const item of poems) {
    const poemName = item.eventId?.name || "Unnamed";
    const htmlDescription = item.eventId?.description || "";

    const lang = (item.eventId?.language || "").toLowerCase();
    let config = getMaxHeightForScreen(lang);

    const extraTitleLines = Math.max(0, Math.ceil(poemName.length / 22) - 1);
    config.firstPageMaxHeight -= (extraTitleLines * 30);

    if (htmlDescription) {
      const isIndic = ["sindhi", "hindi", "marathi"].includes(lang);
      const pages = splitHtmlByHeightPretext(htmlDescription, config, isIndic);

      if (pages.length > 1) {
        pages.forEach((pageContent, index) => {
          processed.push({
            ...item,
            eventId: {
              ...item.eventId,
              name: index === 0 ? item.eventId.name : `${item.eventId.name} (cont.)`,
              description: pageContent,
            },
            pageNumber: index + 1,
            isMultiPage: true,
            totalPages: pages.length,
          });
        });
      } else {
        processed.push({
          ...item,
          eventId: { ...item.eventId, description: htmlDescription },
          isMultiPage: false,
        });
      }
    } else {
      processed.push({ ...item, isMultiPage: false });
    }
  }

  const quotes = Array.isArray(portfolioData?.Quotes)
    ? portfolioData.Quotes
    : [];
  processed.push(
    ...buildGroupedPagesForEntries(
      quotes.map((q) => ({ title: String(q), body: "" })),
      "Quote",
      "Quotes"
    )
  );

  const achievements = Array.isArray(portfolioData?.Achievements)
    ? portfolioData.Achievements
    : [];
  processed.push(
    ...buildGroupedPagesForEntries(
      achievements.map((a) => ({ title: String(a), body: "" })),
      "Achievement",
      "Achievements"
    )
  );

  return processed;
}

function buildGroupedPagesForEntries(entries, eventType, sectionTitle) {
  if (!Array.isArray(entries) || entries.length === 0) return [];
  const config = getMaxHeightForScreen();
  const maxHeight = config.firstPageMaxHeight;
  const pages = [];
  let current = [];
  let currentHtml = "";
  let currentGroupHeight = 0;

  const renderHtml = (list) => {
    return (
      "<div>" +
      list
        .map((entry) => {
          const hasTitle = entry.title && String(entry.title).trim().length > 0;
          const title = hasTitle ? escapeHtml(String(entry.title)) : "";
          const bodyHtml = entry.body
            ? eventType === "FiveWordStory"
              ? `<div>${String(entry.body)}</div>`
              : `<div>${escapeHtml(String(entry.body))}</div>`
            : "";
          if (hasTitle) {
            const bullet =
              eventType === "Quote" || eventType === "Achievement" ? "• " : "";
            return `<div style=\"margin-bottom:10px;\"><div style=\"font-weight:700; margin-bottom:4px;\">${bullet}${title}</div>${bodyHtml}</div>`;
          }
          return `<p style=\"margin:6px 0;\">${bodyHtml || title}</p>`;
        })
        .join("") +
      "</div>"
    );
  };

  for (const v of entries) {
    const entryHeight = measureEntryHeightPretext(v, eventType, config);
    
    if (currentGroupHeight + entryHeight <= maxHeight || current.length === 0) {
      current.push(v);
      currentGroupHeight += entryHeight;
      currentHtml = renderHtml(current);
    } else {
      pages.push(
        pageFromGrouped(eventType, sectionTitle, currentHtml, current)
      );
      current = [v];
      currentGroupHeight = entryHeight;
      currentHtml = renderHtml(current);
    }
  }
  if (current.length > 0) {
    pages.push(pageFromGrouped(eventType, sectionTitle, currentHtml, current));
  }
  return pages;
}

function pageFromGrouped(eventType, sectionTitle, html, entriesForPage) {
  const name = sectionTitle || "";
  return {
    eventType,
    eventId: {
      name,
      description: html,
    },
    tocChildTitles:
      eventType === "FiveWordStory"
        ? Array.isArray(entriesForPage)
          ? entriesForPage
            .map((e) => (e && e.title != null ? String(e.title).trim() : ""))
            .filter((t) => t && t.length > 0)
          : []
        : undefined,
    isMultiPage: false,
  };
}
